<template>
  <el-collapse
      v-model="activeCollapseName"
      class="productdetail-collapse"
      @change="collapseChange">
    <el-row :gutter="40">
      <el-collapse-item
          id="basicInfo"
          title="基本情報"
          name="basicInfo">
        <el-row
            v-if="product"
            :gutter="20"
            class="basic-info">
          <el-col :span="12">
            <BaseTableDetail
                :table-set-list="basicInfo1"
                :display-item="product"
                :editable="editable"
                validate-scope="basicInfo" />
          </el-col>
          <el-col :span="12">
            <BaseTableDetail
                :table-set-list="basicInfo2"
                :display-item="product"
                :editable="editable"
                validate-scope="basicInfo" />
          </el-col>
        </el-row>
      </el-collapse-item>
    </el-row>
    <el-row :gutter="40">
      <el-collapse-item
          v-if="showProductTypeInfo"
          id="productTypeInfo"
          :title="productTypeInfoTitle"
          name="productTypeInfo">
        <el-row
            v-if="product"
            :gutter="20"
            class="basic-info">
          <el-col :span="12">
            <BaseTableDetail
                :table-set-list="productTypeInfo1"
                :display-item="product"
                :editable="editable"
                validate-scope="productTypeInfo" />
          </el-col>
          <el-col :span="12">
            <BaseTableDetail
                :table-set-list="productTypeInfo2"
                :display-item="product"
                :editable="editable"
                validate-scope="productTypeInfo" />
          </el-col>
        </el-row>
      </el-collapse-item>
    </el-row>
  </el-collapse>
</template>

<style scoped>
.radio-button span {
  width: 120px;
}

.productdetail-collapse>.el-row+.el-row {
    margin-top: 40px;
}

</style>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'

import * as define from '@/define'
import common from '@/utils/common'
import utilProduct from '@/utils/product'
// import compare from '@/utils/compareUtils'
import BaseTableDetail from '@/components/molecules/BaseTableDetail'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'

const EDITABLE = Object.freeze({
  [define.MODE.NEW]: Object.freeze({
    productCode: true, productName: true, productType: true, productCategory: true, makerProductCode: true, productYear: true,
    brand: true, subBrand: true, mainCategoryCode: true, category: true, gender: true, salesPeriod: true, onlineDatetime: true,
    offlineDatetime: true, isBuyableOnly: true, isShipment: true, isInventoryControl: true, isSearchable: true, isSearchableIfUnavailable: true,
    attribute: true
  }),
  [define.MODE.EDIT]: Object.freeze({
    productCode: false, productName: true, productType: false, productCategory: false, makerProductCode: true, productYear: true,
    brand: true, subBrand: true, mainCategoryCode: true, category: true, gender: true, salesPeriod: true, onlineDatetime: true,
    offlineDatetime: true, isBuyableOnly: true, isShipment: true, isInventoryControl: true, isSearchable: true, isSearchableIfUnavailable: true,
    attribute: true
  })
})

export default {
  name: 'ProductBasicInfo',
  components: {
    BaseTableDetail
  },
  props: {
    product: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: Number,
      default: define.MODE.NORMAL
    },
    originData: {
      type: Object,
      default: () => ({})
    },
    storePath: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      activeTabName: '',
      activeCollapseName: []
    }
  },
  computed: {
    define: () => define,
    ...mapState({
      customizeMaster: (state) => state.Product.customizeMaster,
      brandMaster: (state) => state.Product.brandMaster,
      genderMaster: (state) => state.Product.genderMaster
    }),
    ...mapGetters({
      productTypeName: 'productTypeName',
      productBundle: 'productBundle',
      productSet: 'productSet',
      productUneditableItem: 'productUneditableItem',
      productCodeMaxLength: 'productCodeMaxLength',
      productEditable: 'productEditable'
    }),
    editable () {
      let returnValue = _.cloneDeep(EDITABLE[this.mode] || {})
      const setValue = this.mode === define.MODE.EDIT
        ? (this.product.isEditable || 0) !== 0
        : true
      // 品番の編集フラグが0である場合、品番に紐づく項目は編集不可にする(サービスサイトで指定した値を除く)
      if ([define.MODE.NEW, define.MODE.EDIT].includes(this.mode)) {
        [].concat(this.basicInfo1, this.basicInfo2, this.productTypeInfo1, this.productTypeInfo2)
          .forEach((obj, data) => {
            if (_.get(obj, 'properties.0.editableId', null) != null) {
              _.set(returnValue, obj.properties[0].editableId, setValue)
            }
          })
        this.productEditable.forEach((key) => {
          _.set(returnValue, key, true)
        })
      }

      // 編集 かつ 既に注文がある場合、サービスサイトで設定されている項目は編集不可にする
      if (this.mode === define.MODE.EDIT && this.product.isOrdered) {
        this.productUneditableItem.forEach((key) => {
          _.set(returnValue, key, false)
        })
      }

      Object.keys(EDITABLE[this.mode] || {}).forEach((key) => {
        if (!EDITABLE[this.mode][key]) _.set(returnValue, key, false)
      })

      return returnValue
    },
    // Detail Table 構成定義
    basicInfo1 () {
      const returnValue = [
        {
          label: '品番コード',
          properties: [ {
            type: 'String', path: 'code', elementType: ELEMENT_TYPE.INPUT,
            editableId: 'productCode', validateRule: this.productCodeMaxLength() != null
              ? `required|regex:^[a-zA-Z0-9!-/:-@¥[-\`{-~]*$|max:${this.productCodeMaxLength() || 0}`
              : `required|regex:^[a-zA-Z0-9!-/:-@¥[-\`{-~]*$|max:50`, validateAs: '品番コード'
          }]
        },
        {
          label: '商品名',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.INPUT, path: 'name',
            editableId: 'productName',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.NAME
          }]
        },
        {
          label: '商品名（カナ）',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.INPUT, path: 'nameKana',
            editableId: 'productName',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.NAME_KANA
          }]
        },
        {
          label: '商品名略称',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.INPUT, path: 'productNameAbbreviation',
            editableId: 'productName',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.ABBREVIATION_NAME
          }]
        },
        {
          label: '基幹商品名',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.INPUT, path: 'coreProductName',
            editableId: 'productName',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.CORE_NAME
          }]
        },
        {
          label: '商品種類',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX, path: 'productType',
            editableId: 'productType', validateRule: 'required', validateAs: '商品区分',
            codeName: 'm_product.product_type', selectCode: 'm_product.product_type',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.PRODUCT_TYPE
          }]
        },
        {
          label: '商品区分',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX, path: 'productCategory',
            editableId: 'productCategory', validateRule: 'required', validateAs: '商品区分',
            codeName: 'm_product.product_category', selectCode: 'm_product.product_category',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.PRODUCT_CATEGORY,
            change: this.categoryChange
          }]
        },
        {
          label: '他社品番',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.INPUT, path: 'makerProductCode',
            editableId: 'makerProductCode',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.MAKER_PRODUCT_CODE
          }]
        },
        {
          label: '年度',
          properties: [ {
            type: 'Date', elementType: ELEMENT_TYPE.YEAR, path: 'productYear', dateFormat: 'YYYY',
            editableId: 'productYear',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.PRODUCT_YEAR
          }]
        },
        {
          label: 'ブランド',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX, path: 'brand.code',
            editableId: 'brand', selectCode: () => this.brandMaster,
            codeName: (code) => (this.brandMaster.find(element => code === element.code) || {}).name,
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.BRAND
          }]
        },
        {
          label: 'サブブランド',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX, path: 'subBrand.code',
            editableId: 'subBrand', selectCode: () => this.brandMaster,
            codeName: (code) => (this.brandMaster.find(element => code === element.code) || {}).name,
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.SUB_BRAND
          }]
        }
        // レーベルは現在未実装
        // {
        //   label: 'レーベル',
        //   properties: [ {
        //     type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX, path: '',
        //     selectCode: () => _.map(this.productLabelList, (val) => ({ code: val.productLabelCode, name: val.productLabelName })),
        //     codeName: (code) => _.chain(this.productLabelList)
        //       .find(val => val.productLabelCode === code)
        //       .get('productLabelName')
        //       .value()
        //   }]
        // },
        // {
        //   label: 'サブレーベル',
        //   properties: [ {
        //     type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX, path: '',
        //     selectCode: () => _.map(this.optionsForProductSubLabel, (val) => ({ code: val.productSubLabelCode, name: val.productSubLabelName })),
        //     codeName: (code) => _.chain(this.optionsForProductSubLabel)
        //       .find(val => val.productSubLabelCode === code)
        //       .get('productSubLabelName')
        //       .value()
        //   }]
        // }
      ]

      let index = 1
      // カスタマイズ属性を設定
      if (this.customizeMaster != null &&
        this.$isFunctionShown(define.PermissionKeys.Product.Detail.BasicInfo.ATTRIBUTE)) {
        this.customizeMaster.filter((element) => {
          // 商品種類共通のカスタマイズ属性である
          // 表示位置が左の段であること
          // 基本情報タブである
          return (element.functionKey === define.CUSTOMIZE_PROPERTY_KEY.PRODUCT.BASIC_INFO.COMMON.LEFT)
        }).sort((a, b) => {
          return a.displayOrder - b.displayOrder
        }).forEach((customizeObj) => {
          let emptyCellNum = customizeObj.displayOrder - index
          while (emptyCellNum > 1) {
            returnValue.push(utilProduct.getCustomizeEmptyColumn())
            emptyCellNum -= 1
          }
          returnValue.push(utilProduct.getCustomizeColumn(this.product, customizeObj, 'attributes', 'attribute', this.storePath, 'ProductDetail/productDetail'))

          index = customizeObj.displayOrder
        })
      }

      return returnValue
    },
    basicInfo2 () {
      const returnValue = [
        {
          label: '主カテゴリ',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX, path: 'mainCategoryCode',
            editableId: 'mainCategoryCode', codeName: 'm_product.main_category', selectCode: 'm_product.main_category',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.MAIN_CATEGORY
          }]
        },
        {
          label: 'カテゴリ',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX, path: 'category', multiple: true,
            editableId: 'category', selectCode: 'm_product.category', codeName: 'm_product.category',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.CATEGORY
          }]
        },
        {
          label: '性別',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX, path: 'gender.code',
            editableId: 'gender', selectCode: this.genderMaster,
            codeName: (code) => {
              return (this.genderMaster.find(element => code === element.code) || {}).name
            },
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.GENDER
          }]
        },
        {
          label: '販売開始日',
          properties: [ {
            type: 'Date', elementType: ELEMENT_TYPE.DATE, path: 'salesPeriodFrom',
            editableId: 'salesPeriod',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.SALES_PERIOD_FROM,
            dateFormat: 'YYYY/MM/DD'
          }]
        },
        {
          label: '販売終了日',
          properties: [ {
            type: 'Date', elementType: ELEMENT_TYPE.DATE, path: 'salesPeriodTo',
            editableId: 'salesPeriod',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.SALES_PERIOD_TO,
            dateFormat: 'YYYY/MM/DD'
          }]
        },
        {
          label: 'オンラインフラグ',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.CHECK_BOX, path: 'isOnline',
            editableId: 'onlineDatetime',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.IS_ONLINE
          }]
        },
        {
          label: 'オンライン日時',
          properties: [ {
            type: 'Date', elementType: ELEMENT_TYPE.DATE_TIME, path: 'onlineDatetime',
            editableId: 'onlineDatetime',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.ONLINE_DATETIME,
            dateFormat: 'YYYY/MM/DD HH:mm:ss'
          }]
        },
        {
          label: 'オフライン日時',
          properties: [ {
            type: 'Date', elementType: ELEMENT_TYPE.DATE_TIME, path: 'offlineDatetime',
            editableId: 'offlineDatetime',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.OFFLINE_DATETIME,
            dateFormat: 'YYYY/MM/DD HH:mm:ss'
          }]
        },
        {
          label: '単体販売可否',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.CHECK_BOX, path: 'isBuyableOnly',
            editableId: 'isBuyableOnly',
            selectCode: 'm_product.is_buyable_only',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.IS_BUYABLE_ONLY
          }]
        },
        {
          label: '出荷有無',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.CHECK_BOX, path: 'isShipment',
            editableId: 'isShipment',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.IS_SHIPMENT
          }]
        },
        {
          label: '在庫管理有無',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.CHECK_BOX, path: 'isInventoryControl',
            editableId: 'isInventoryControl',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.IS_INVENTORY_CONTROL
          }]
        },
        {
          label: '検索可否',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.CHECK_BOX, path: 'isSearchable',
            editableId: 'isSearchable',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.IS_SEARCHABLE
          }]
        },
        {
          label: '販売不可時の検索可否',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.CHECK_BOX, path: 'isSearchableIfUnavailable',
            editableId: 'isSearchableIfUnavailable',
            funcId: define.PermissionKeys.Product.Detail.BasicInfo.IS_SEARCHABLE_IF_UNAVAILABLE
          }]
        }
      ]

      let index = 1
      if (this.customizeMaster != null &&
      this.$isFunctionShown(define.PermissionKeys.Product.Detail.BasicInfo.ATTRIBUTE)) {
        this.customizeMaster.filter((element) => {
          // 商品種類共通のカスタマイズ属性である
          // 基本情報タブである
          // 表示位置が右の段であること
          return (element.functionKey === define.CUSTOMIZE_PROPERTY_KEY.PRODUCT.BASIC_INFO.COMMON.RIGHT)
        }).sort((a, b) => {
          return a.displayOrder - b.displayOrder
        }).forEach((customizeObj) => {
          let emptyCellNum = customizeObj.displayOrder - index
          while (emptyCellNum > 1) {
            returnValue.push(utilProduct.getCustomizeEmptyColumn())
            emptyCellNum -= 1
          }
          returnValue.push(utilProduct.getCustomizeColumn(this.product, customizeObj, 'attributes', 'attribute', this.storePath, 'ProductDetail/productDetail'))

          index = customizeObj.displayOrder
        })
      }

      return returnValue
    },
    activeCollapseKey () {
      return 'productDetailCollapse'
    },
    brandOptions () {
      return _.map(this.brandMaster, (val) => { return { code: val.code, name: val.name } })
    },
    productTypeInfoTitle () {
      // 商品種類名 ＋ 「情報」
      return `${this.productTypeName(this.product.productType)}情報`
    },
    showProductTypeInfo () {
      // 商品種類が選択されている
      // 商品種類にひもづくカスタマイズ属性が存在する
      if (this.product.productType != null &&
          this.customizeMaster.findIndex((element) => {
            return (element.functionKey === define.CUSTOMIZE_PROPERTY_KEY.PRODUCT.BASIC_INFO.PRODUCT_TYPE.LEFT.replace('{productType}', this.product.productType)) ||
              (element.functionKey === define.CUSTOMIZE_PROPERTY_KEY.PRODUCT.BASIC_INFO.PRODUCT_TYPE.RIGHT.replace('{productType}', this.product.productType))
          }) >= 0) {
        return true
      }
      return false
    },
    // Detail Table 構成定義
    productTypeInfo1 () {
      if (this.customizeMaster == null) {
        return
      }
      const returnValue = []
      let index = 1
      // カスタマイズ属性を設定
      this.customizeMaster.filter((element) => {
        // 対象の商品種類のカスタマイズ属性である
        // 基本情報タブである
        // 表示位置が左の段であること
        return (element.functionKey === define.CUSTOMIZE_PROPERTY_KEY.PRODUCT.BASIC_INFO.PRODUCT_TYPE.LEFT.replace('{productType}', this.product.productType))
      }).sort((a, b) => {
        return a.displayOrder - b.displayOrder
      }).forEach((customizeObj) => {
        let emptyCellNum = customizeObj.displayOrder - index
        while (emptyCellNum > 1) {
          returnValue.push(utilProduct.getCustomizeEmptyColumn())
          emptyCellNum -= 1
        }
        returnValue.push(utilProduct.getCustomizeColumn(this.product, customizeObj, 'attributes', 'attribute', this.storePath, 'ProductDetail/productDetail'))

        index = customizeObj.displayOrder
      })

      return returnValue
    },
    productTypeInfo2 () {
      if (this.customizeMaster == null) {
        return
      }
      const returnValue = []
      let index = 1
      // カスタマイズ属性を設定
      this.customizeMaster.filter((element) => {
        // 対象の商品種類のカスタマイズ属性である
        // 基本情報タブである
        // 表示位置が右の段であること
        return (element.functionKey === define.CUSTOMIZE_PROPERTY_KEY.PRODUCT.BASIC_INFO.COMMON.RIGHT.replace('{productType}', this.product.productType))
      }).sort((a, b) => {
        return a.displayOrder - b.displayOrder
      }).forEach((customizeObj) => {
        let emptyCellNum = customizeObj.displayOrder - index
        while (emptyCellNum > 1) {
          returnValue.push(utilProduct.getCustomizeEmptyColumn())
          emptyCellNum -= 1
        }
        returnValue.push(utilProduct.getCustomizeColumn(this.product, customizeObj, 'attributes', 'attribute', this.storePath, 'ProductDetail/productDetail'))

        index = customizeObj.displayOrder
      })

      return returnValue
    }
  },
  created () {
    this.activeCollapseName = _.split(common.getLocalStorage(this.activeCollapseKey), ',')
  },
  methods: {
    /**
     * collapse の 開閉状態を localStorage に保存します。
     */
    collapseChange: function () {
      common.setLocalStorage(this.activeCollapseKey, this.activeCollapseName.join())
      this.activeCollapseName = common.getLocalStorage(this.activeCollapseKey).split(',')
    },
    validateAll: async function () {
      const forms = ['form1', 'form2']
      let result = true
      for (const form of forms) {
        try {
          if (!await this.$refs[form].validate()) {
            result = false
          }
        } catch (err) {
          result = false
        }
      }
      return result
    },
    categoryChange: function (val) {
      // 値を初期化する
      _.set(this.product, 'colorGroupCode', null)
      _.set(this.product, 'mainColorCode', null)
      _.set(this.product, 'sizeGroupCode', null)
      _.set(this.product, 'colorVariations', [])
      _.set(this.product, 'sizeVariations', [])

      // セット
      // バンドル商品である場合、カラーコードがマスタに登録されていないため、nullとなってしまうため、code,nameを置き換える
      if ([define.PRODUCT_CATEGORY.BUNDLE, define.PRODUCT_CATEGORY.SET].includes(val)) {
        const useInfo = (val === define.PRODUCT_CATEGORY.BUNDLE) ? this.productBundle : this.productSet

        _.set(this.product, 'colorGroupCode', useInfo.colorCode)
        _.set(this.product, 'sizeGroupCode', useInfo.sizeCode)

        const sku = {
          color: {
            code: useInfo.colorCode,
            name: useInfo.name
          },
          size: {
            code: useInfo.sizeCode,
            name: useInfo.name
          },
          isInventoryControl: 0,
          components: [],
          skuType: null,
          productDetail: '',
          productDescription: '',
          attributes: []
        }
        const fku = {
          color: { code: useInfo.colorCode, name: useInfo.name },
          skus: [sku],
          images: []
        }
        _.set(this.product, 'fkus', [fku])
      } else {
        _.set(this.product, 'fkus', [])
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/product/ProductBasicInfo.vue